"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  HINT,
  LABEL,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  COMMON_POINT_SIZES,
  convertFontSize,
  cssValue,
  FONT_UNITS,
  type FontBases,
  type FontUnit,
  toPx,
} from "@/lib/tool-kit/converters/font-size";

/** English strings. A translated content file overrides these through `ui`. */
export const FONT_SIZE_CONVERTER_STRINGS = {
  sizeLabel: "Font size",
  unitLabel: "Unit",
  units: {
    pt: "Points (pt)",
    px: "Pixels (px)",
    em: "em",
    rem: "rem",
    percent: "Percent (%)",
    mm: "Millimeters (mm)",
    cm: "Centimeters (cm)",
    in: "Inches (in)",
    pc: "Picas (pc)",
  },
  rootLabel: "Root font size in px (sets 1rem)",
  parentLabel: "Parent font size in px (sets 1em and 100%)",
  basesHint:
    "Browsers start at 16 px. Change these if your stylesheet sets another size on the html element or on the parent.",
  invalidSize: "Enter the font size as a number, such as 12 or 1.5.",
  invalidBase: "Root and parent font sizes must be numbers greater than zero.",
  result: "{value} {unit} in every unit",
  unitColumn: "Unit",
  valueColumn: "Size",
  cssColumn: "CSS",
  copyColumn: "Copy",
  copy: "Copy",
  copied: "Copied!",
  working: "Working",
  workingPt: "{value} pt × 96 ÷ 72 = {px} px",
  workingPc: "{value} pc × 16 = {px} px",
  workingIn: "{value} in × 96 = {px} px",
  workingMm: "{value} mm × 96 ÷ 25.4 = {px} px",
  workingCm: "{value} cm × 96 ÷ 2.54 = {px} px",
  workingEm: "{value} em × {parent} px = {px} px",
  workingRem: "{value} rem × {root} px = {px} px",
  workingPercent: "{value}% × {parent} px ÷ 100 = {px} px",
  workingToPt: "{px} px × 72 ÷ 96 = {pt} pt",
  workingToEm: "{px} px ÷ {parent} px = {em} em",
  workingToRem: "{px} px ÷ {root} px = {rem} rem",
  workingToMm: "{px} px × 25.4 ÷ 96 = {mm} mm",
  wordNote:
    "Word on Windows and Google Docs size type in points and treat an inch as 96 pixels, so at 100% zoom 12-point text is drawn at the same size as 16px text in a browser.",
  tableHeading: "Common point sizes",
  tableHint: "em, rem and % use the root and parent sizes above.",
  ptColumn: "Points",
  pxColumn: "Pixels",
  emColumn: "em",
  remColumn: "rem",
  percentColumn: "%",
  mmColumn: "mm",
} as const;

type Strings = typeof FONT_SIZE_CONVERTER_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const DECIMALS = { maximumFractionDigits: 4 };

const TO_PX_WORKING: Record<FontUnit, keyof Strings | null> = {
  pt: "workingPt",
  pc: "workingPc",
  in: "workingIn",
  mm: "workingMm",
  cm: "workingCm",
  em: "workingEm",
  rem: "workingRem",
  percent: "workingPercent",
  px: null,
};

function workingLines(
  value: number,
  unit: FontUnit,
  bases: FontBases,
  strings: Strings,
  fmt: Fmt
): string[] {
  const all = convertFontSize(value, unit, bases);
  const px = toPx(value, unit, bases);
  const values = {
    value: fmt(value, DECIMALS),
    px: fmt(px, DECIMALS),
    pt: fmt(all.pt, DECIMALS),
    em: fmt(all.em, DECIMALS),
    rem: fmt(all.rem, DECIMALS),
    mm: fmt(all.mm, DECIMALS),
    parent: fmt(bases.parent, DECIMALS),
    root: fmt(bases.root, DECIMALS),
  };
  const lines: string[] = [];
  const first = TO_PX_WORKING[unit];
  if (first) {
    lines.push(t(strings[first] as string, values));
  }
  const rest: [FontUnit, keyof Strings][] = [
    ["pt", "workingToPt"],
    ["em", "workingToEm"],
    ["rem", "workingToRem"],
    ["mm", "workingToMm"],
  ];
  for (const [target, key] of rest) {
    if (target !== unit) {
      lines.push(t(strings[key] as string, values));
    }
  }
  return lines;
}

function readPositive(text: string): number | null {
  const value = parseNumberInput(text);
  return value !== null && value > 0 ? value : null;
}

function CommonSizes({
  bases,
  strings,
}: {
  bases: FontBases;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <section className={`${CARD} space-y-3`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <p className={HINT}>{strings.tableHint}</p>
      <DataTable
        head={[
          strings.ptColumn,
          strings.pxColumn,
          strings.emColumn,
          strings.remColumn,
          strings.percentColumn,
          strings.mmColumn,
        ]}
        rows={COMMON_POINT_SIZES.map((points) => {
          const all = convertFontSize(points, "pt", bases);
          return [
            fmt(points, DECIMALS),
            fmt(all.px, { maximumFractionDigits: 2 }),
            fmt(all.em, { maximumFractionDigits: 3 }),
            fmt(all.rem, { maximumFractionDigits: 3 }),
            fmt(all.percent, { maximumFractionDigits: 1 }),
            fmt(all.mm, { maximumFractionDigits: 2 }),
          ];
        })}
      />
    </section>
  );
}

export default function FontSizeConverterWidget() {
  const strings = useToolUi(FONT_SIZE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [sizeText, setSizeText] = useState("12");
  const [unit, setUnit] = useState<FontUnit>("pt");
  const [rootText, setRootText] = useState("16");
  const [parentText, setParentText] = useState("16");

  const value = parseNumberInput(sizeText);
  const root = readPositive(rootText);
  const parent = readPositive(parentText);
  const bases: FontBases | null =
    root !== null && parent !== null ? { root, parent } : null;

  let error: string | null = null;
  if (value === null) {
    error = strings.invalidSize;
  } else if (bases === null) {
    error = strings.invalidBase;
  }

  const all =
    value !== null && bases !== null
      ? convertFontSize(value, unit, bases)
      : null;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="fs-size"
          label={strings.sizeLabel}
          numeric
          onChange={setSizeText}
          value={sizeText}
        />
        <SelectField
          id="fs-unit"
          label={strings.unitLabel}
          onChange={setUnit}
          options={FONT_UNITS.map((each) => ({
            value: each,
            label: strings.units[each],
          }))}
          value={unit}
        />
        <TextField
          id="fs-root"
          label={strings.rootLabel}
          numeric
          onChange={setRootText}
          value={rootText}
        />
        <TextField
          id="fs-parent"
          label={strings.parentLabel}
          numeric
          onChange={setParentText}
          value={parentText}
        />
      </div>
      <p className={HINT}>{strings.basesHint}</p>
      {error ? <ErrorNotice>{error}</ErrorNotice> : null}
      {all !== null && value !== null && bases !== null ? (
        <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
          <h2 className={WIDGET_H2}>
            {t(strings.result, {
              value: fmt(value, DECIMALS),
              unit: unit === "percent" ? "%" : unit,
            })}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH} scope="col">
                    {strings.unitColumn}
                  </th>
                  <th className={TH} scope="col">
                    {strings.valueColumn}
                  </th>
                  <th className={TH} scope="col">
                    {strings.cssColumn}
                  </th>
                  <th className={TH} scope="col">
                    <span className="sr-only">{strings.copyColumn}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {FONT_UNITS.map((each) => (
                  <tr
                    className={each === unit ? "bg-primary/10 font-medium" : ""}
                    key={each}
                  >
                    <td className={TD}>{strings.units[each]}</td>
                    <td className={`${TD} font-mono`}>
                      {fmt(all[each], DECIMALS)}
                    </td>
                    <td className={`${TD} font-mono`}>
                      {cssValue(all[each], each)}
                    </td>
                    <td className={TD}>
                      <CopyButton
                        copiedLabel={strings.copied}
                        label={strings.copy}
                        small
                        text={cssValue(all[each], each)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2">
            <p className={LABEL}>{strings.working}</p>
            <ul className="space-y-1 font-mono text-foreground text-sm">
              {workingLines(value, unit, bases, strings, fmt).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <p className={HINT}>{strings.wordNote}</p>
        </section>
      ) : null}
      {bases !== null ? <CommonSizes bases={bases} strings={strings} /> : null}
    </div>
  );
}
